"use client";
import React, { useState, useEffect, useMemo, useContext } from "react";
import { message as staticMessage, App } from "antd";
import Link from "next/link";
import Image from "next/image";
import CMSLink from "../common/CMSLink";
import ShareButton from "../shareButton";
import FAQSection, { extractFaqItems } from "../FAQSection/FAQSection";
import styles from "./blog.module.css";
import organiserArt from "./organiserblog.png";
import filmstripArt from "./clip.png";
import SafeHtml from "../CMS/SafeHtml";
import AppImage from "../common/AppImage";
import dayjs from "dayjs";
import {
  buildDynamicContext,
  resolveDynamicVariables,
} from "@/lib/replaceDynamicValues";
import { IconArrowLeft, IconArrowRight, IconCopy } from "@/lib/icons";
import { locationcontext } from "@/lib/context";
import { toCitySlug } from "@/lib/citySlug";
import { EXTERNAL_REL } from "@/lib/externalLink";

export default function BlogDetail({
  cmsData,
  faqData,
  dynamicContext: passedContext,
}) {
  const [scrollProgress, setScrollProgress] = useState(0);
  const { location } = useContext(locationcontext) || {};

  useEffect(() => {
    const handleScroll = () => {
      const totalHeight =
        document.documentElement.scrollHeight - window.innerHeight;
      if (totalHeight > 0) {
        const progress = (window.scrollY / totalHeight) * 100;
        setScrollProgress(Math.min(100, Math.max(0, progress)));
      }
    };
    window.addEventListener("scroll", handleScroll);
    return () => window.removeEventListener("scroll", handleScroll);
  }, []);

  if (!cmsData) return null;

  const dynamicContext =
    passedContext || buildDynamicContext({ blog: cmsData });

  const resolvedCmsData =
    resolveDynamicVariables(cmsData, dynamicContext) || {};

  const {
    title = "Blog Detail",
    image = [],
    detail_page_image,
    description = "",
    previous_blog,
    next_blog,
  } = resolvedCmsData;

  const extractImgUrl = (val) => {
    if (!val) return "";
    if (typeof val === "string") return val.trim();
    if (Array.isArray(val)) {
      for (const item of val) {
        const u = extractImgUrl(item);
        if (u) return u;
      }
      return "";
    }
    if (typeof val === "object" && val !== null) {
      return (
        val.url ||
        val.src ||
        val.md ||
        val.sd ||
        val.thumbnail ||
        val.name ||
        ""
      );
    }
    return "";
  };

  const resolveBlogUrl = (blogObj) => {
    if (!blogObj) return "";
    if (typeof blogObj === "string") {
      const s = blogObj.trim();
      if (!s) return "";
      if (s.startsWith("/")) return s;
      if (s.startsWith("blogs/")) return `/${s}`;
      return `/blogs/${s}`;
    }
    if (Array.isArray(blogObj)) {
      return resolveBlogUrl(blogObj[0]);
    }
    const rawUrl =
      blogObj.url?.url || blogObj.url || blogObj.slug || blogObj.link || "";
    if (!rawUrl) return "";
    if (Array.isArray(rawUrl)) {
      return `/${rawUrl.join("/")}`;
    }
    const strUrl = String(rawUrl).trim();
    if (strUrl.startsWith("/")) return strUrl;
    if (strUrl.startsWith("blogs/")) return `/${strUrl}`;
    return `/blogs/${strUrl}`;
  };

  const resolveBlogTitle = (blogObj) => {
    if (!blogObj) return "";
    if (typeof blogObj === "string") return blogObj;
    if (Array.isArray(blogObj)) return resolveBlogTitle(blogObj[0]);
    if (typeof blogObj === "object" && blogObj !== null) {
      return blogObj.title || blogObj.name || blogObj.slug || "";
    }
    return String(blogObj);
  };

  const prevUrl = resolveBlogUrl(previous_blog);
  const prevTitle = resolveBlogTitle(previous_blog);
  const nextUrl = resolveBlogUrl(next_blog);
  const nextTitle = resolveBlogTitle(next_blog);

  // Extract author_name, authorName, or meta author safely
  const resolveAuthorName = (data) => {
    if (!data) return "Tykkit Team";
    const raw =
      data.authorName ||
      data.author?.name ||
      (typeof data.author === "string" ? data.author : null) ||
      data.written_by?.name ||
      (typeof data.written_by === "string" ? data.written_by : null) ||
      data.created_by?.name ||
      (typeof data.created_by === "string" ? data.created_by : null) ||
      "Tykkit Team";
    if (typeof raw === "string") return raw;
    if (typeof raw === "object" && raw !== null) {
      return raw.name || raw.title || raw.username || "Tykkit Team";
    }
    return String(raw || "Tykkit Team");
  };

  const authorName = resolveAuthorName(resolvedCmsData);

  const rawAvatar =
    resolvedCmsData.author?.avatar || resolvedCmsData.author_avatar;
  const avatarUrl = extractImgUrl(rawAvatar);
  const authorInitial =
    typeof authorName === "string" && authorName.trim()
      ? authorName.trim().charAt(0).toUpperCase()
      : "T";
  const authorAvatar = avatarUrl || authorInitial;

  // Extract tags array
  const rawTags = resolvedCmsData.tags || resolvedCmsData.tag || [];
  const allTags = Array.isArray(rawTags)
    ? rawTags.filter(Boolean)
    : typeof rawTags === "string"
      ? [rawTags]
      : [];

  // Date
  const rawDate =
    resolvedCmsData.date ||
    resolvedCmsData.published_date ||
    resolvedCmsData.created_at;
  const pubDate = (() => {
    if (typeof rawDate === "string" && dayjs(rawDate).isValid()) {
      const hasTime = rawDate.includes("T")
        ? !rawDate.includes("T00:00:00") && !rawDate.endsWith("T00:00:00.000Z")
        : rawDate.includes(" ")
          ? !rawDate.includes(" 00:00:00") && !rawDate.includes(" 0:00:00")
          : false;

      return hasTime
        ? dayjs(rawDate).format("MMMM D, YYYY hh:mm A")
        : dayjs(rawDate).format("MMMM D, YYYY");
    }
    return typeof rawDate === "string"
      ? rawDate.split("T")[0]
      : typeof rawDate === "number"
        ? String(rawDate)
        : "";
  })();

  const detailImg =
    extractImgUrl(detail_page_image) ||
    extractImgUrl(resolvedCmsData.detail_page_image) ||
    extractImgUrl(resolvedCmsData.content?.detail_page_image) ||
    extractImgUrl(resolvedCmsData.blog?.detail_page_image);

  const coverImg =
    extractImgUrl(image) ||
    extractImgUrl(resolvedCmsData.feature_image) ||
    extractImgUrl(resolvedCmsData.cover_image) ||
    extractImgUrl(resolvedCmsData.thumbnail) ||
    extractImgUrl(resolvedCmsData.bg_image) ||
    extractImgUrl(resolvedCmsData.meta?.feature_image) ||
    extractImgUrl(resolvedCmsData.meta?.image) ||
    "";

  // The detail page leads with the cover `image`; cards use `listing_image`
  // (BlogCard.js). `detail_page_image` remains a fallback for older posts.
  const img = coverImg || detailImg;

  const antdApp = App?.useApp ? App.useApp() : null;
  const messageApi = antdApp?.message || staticMessage;

  const handleCopyLink = async () => {
    const pageUrl = typeof window !== "undefined" ? window.location.href : "";
    if (!pageUrl) return;
    try {
      await navigator.clipboard.writeText(pageUrl);
      messageApi?.success?.("Link copied to clipboard!");
    } catch {
      messageApi?.error?.("Failed to copy link.");
    }
  };

  // Parse description for ToC
  const { parsedDescription, toc } = useMemo(() => {
    const tocItems = [];
    let headingIndex = 0;
    const newDesc =
      description?.replace(/<h2[^>]*>(.*?)<\/h2>/gi, (match, innerHtml) => {
        headingIndex++;
        const id = `toc-heading-${headingIndex}`;
        const text = innerHtml
          .replace(/<[^>]+>/g, "")
          .replace(/&nbsp;/g, " ")
          .trim();
        if (text) {
          tocItems.push({ id, text });
        }
        return `<h2 id="${id}" style="scroll-margin-top: 100px;">${innerHtml}</h2>`;
      }) || "";
    return { parsedDescription: newDesc, toc: tocItems };
  }, [description]);

  // Extract FAQs if present on blog CMS data or passed via FAQ section
  const blogFaqs = useMemo(() => {
    const fromFaq = extractFaqItems(faqData);
    if (fromFaq.length > 0) {
      return fromFaq.filter(
        (f) => f && typeof f === "object" && f.question && f.answer,
      );
    }
    const fromCms = extractFaqItems(resolvedCmsData);
    if (fromCms.length > 0) {
      return fromCms.filter(
        (f) => f && typeof f === "object" && f.question && f.answer,
      );
    }
    return [];
  }, [faqData, resolvedCmsData]);

  const faqTitle =
    faqData?.title ||
    resolvedCmsData?.faq_title ||
    resolvedCmsData?.faqTitle ||
    "Frequently Asked Questions";

  return (
    <>
      {/* Top Reading Progress Indicator */}
      <div
        className={styles.progressBar}
        style={{ width: `${scrollProgress}%` }}
      />

      <div className={styles.detailWrapper}>
        <div className={styles.detailContainer}>
          <div
            className={`${styles.detailGrid} ${
              toc.length > 0 ? "" : styles.detailGridNoToc
            }`}
          >
            {/* Left Column (Table of Contents) */}
            {toc.length > 0 && (
              <aside className={`${styles.sidebar} ${styles.tocSidebar}`}>
                <div className={styles.tocContainer}>
                  <p className={styles.tocTitle}>Table of Contents</p>
                  <ul className={styles.tocList}>
                    {toc.map((item, index) => (
                      <li key={item.id} className={styles.tocItem}>
                        <span className={styles.tocNumber}>{index + 1}.</span>
                        <a
                          href={`#${item.id}`}
                          onClick={(e) => {
                            e.preventDefault();
                            document
                              .getElementById(item.id)
                              ?.scrollIntoView({ behavior: "smooth" });
                          }}
                          className={styles.tocLink}
                        >
                          {item.text}
                        </a>
                      </li>
                    ))}
                  </ul>
                </div>
              </aside>
            )}

            {/* Main Article Column */}
            <div className={` ${styles.mainColumn}`}>
              {/* Article Title & Author Meta */}
              <header className={styles.articleMetaHeader}>
                <div className={styles.titleRow}>
                  <div className={styles.titleBlock}>
                    <h1 className={styles.detailTitle}>{title}</h1>
                    <p className={styles.pubDate}>
                      {authorName} &bull; {pubDate}
                    </p>
                  </div>

                  <div className={styles.titleActions}>
                    <ShareButton
                      eventDetails={{ name: title, image: img }}
                      shareType="blog"
                      showLabel={true}
                      label="Share"
                    />
                    <button
                      type="button"
                      onClick={handleCopyLink}
                      className={`${styles.shareBtn} ${styles.copyLinkBtn}`}
                    >
                      <IconCopy size={16} /> Copy Link
                    </button>
                  </div>
                </div>
              </header>

              {/* Hero Image */}
              {img && (
                <div className={styles.heroCard}>
                  <AppImage
                    src={img}
                    useUrl
                    alt={title}
                    fill
                    priority
                    style={{ objectFit: "cover" }}
                  />
                </div>
              )}

              {/* Article Content */}
              <div>
                <SafeHtml html={parsedDescription} />
              </div>

              {/* In-Article FAQs */}
              {blogFaqs.length > 0 && (
                <FAQSection
                  data={{
                    faqs: blogFaqs,
                    title: faqTitle,
                  }}
                  inArticle={true}
                />
              )}

              {/* Bottom Tags */}
              {allTags.length > 0 && (
                <div
                  className={styles.articleTagsBottom}
                  style={{
                    display: "flex",
                    gap: "4px",
                    flexWrap: "wrap",
                    alignItems: "center",
                    justifyContent: "center",
                    textAlign: "center",
                  }}
                >
                  <strong>Explore More:</strong>
                  {allTags.map((tagObj, idx) => {
                    const isObj = typeof tagObj === "object" && tagObj !== null;
                    const label = isObj
                      ? tagObj.label ||
                        tagObj.title ||
                        tagObj.name ||
                        tagObj.tag ||
                        "Tag"
                      : String(tagObj);

                    /*
                    // Redirect logic kept commented for future use
                    const linkUrl = isObj
                      ? tagObj.url_obj || tagObj.url || tagObj.link
                      : undefined;

                    let href = "#";
                    if (linkUrl) {
                      href =
                        typeof linkUrl === "string"
                          ? linkUrl
                          : linkUrl.url || linkUrl.link || "#";
                    } else if (!isObj) {
                      const citySlug = toCitySlug(
                        location || dynamicContext?.city_slug,
                      );
                      href = `/${citySlug}/${tagObj}`;
                    }
                    if (
                      href !== "#" &&
                      !href.startsWith("/") &&
                      !href.startsWith("http")
                    )
                      href = `/${href}`;
                    */

                    return (
                      <span key={idx}>
                        {/* <Link href={href} className={styles.tagLink}>
                          {label}
                        </Link> */}
                        <span className={styles.tagLink}>
                          {label}
                        </span>
                        {idx < allTags.length - 1 && (
                          <span style={{ color: "#64748b" }}>,</span>
                        )}
                      </span>
                    );
                  })}
                </div>
              )}

              {/* Bottom Nav Row */}
              <div className={styles.navSection}>
                <div className={styles.navGrid}>
                  {prevUrl ? (
                    <CMSLink
                      config={{
                        url: prevUrl,
                        type: "internal",
                      }}
                      className={styles.navCard}
                    >
                      <span className={styles.navCardLabel}>
                        <IconArrowLeft size={14} /> Previous Article
                      </span>
                      <span className={styles.navCardTitle}>{prevTitle}</span>
                    </CMSLink>
                  ) : (
                    <div />
                  )}

                  {nextUrl ? (
                    <CMSLink
                      config={{
                        url: nextUrl,
                        type: "internal",
                      }}
                      className={styles.navCard}
                      style={{ textAlign: "right" }}
                    >
                      <span
                        className={styles.navCardLabel}
                        style={{ justifyContent: "flex-end" }}
                      >
                        Next Article <IconArrowRight size={14} />
                      </span>
                      <span className={styles.navCardTitle}>{nextTitle}</span>
                    </CMSLink>
                  ) : (
                    <div />
                  )}
                </div>
              </div>
            </div>

            {/* Right Column (Promo cards) */}
            <aside className={`${styles.sidebar} ${styles.promoSidebar}`}>
              {process.env.NEXT_PUBLIC_BACKSTAGE_URL && (
                <div
                  className={`${styles.promoWidget} ${styles.promoWidgetOrganiser}`}
                >
                  <h3 className={styles.promoTitle}>Are you an Organiser?</h3>
                  <p className={styles.promoText}>
                    Host concerts, comedy shows, workshops and more. List your
                    event on Tykkit and start selling tickets in minutes.
                  </p>
                  <a
                    href={process.env.NEXT_PUBLIC_BACKSTAGE_URL}
                    target="_blank"
                    rel={EXTERNAL_REL}
                    className={styles.promoBtn}
                  >
                    <span>List Your Event</span>
                  </a>
                  <div
                    className={`${styles.promoArt} ${styles.promoArtOrganiser}`}
                    aria-hidden="true"
                  >
                    <Image src={organiserArt} alt="" fill sizes="240px" />
                  </div>
                </div>
              )}

              <div
                className={`${styles.promoWidget} ${styles.promoWidgetEvents}`}
              >
                <h3 className={styles.promoTitle}>Discover Live Events</h3>
                <p className={styles.promoText}>
                  Looking for concerts, stand-up comedy, theatre, or workshops
                  in your city? Book tickets instantly on Tykkit.
                </p>
                <Link href="/" className={styles.promoBtn}>
                  <span>Explore Events</span>
                </Link>
                <div
                  className={`${styles.promoArt} ${styles.promoArtFilmstrip}`}
                  aria-hidden="true"
                >
                  <Image src={filmstripArt} alt="" fill sizes="340px" />
                </div>
              </div>
            </aside>
          </div>
        </div>
      </div>
    </>
  );
}
